import { Component, ViewEncapsulation, inject, effect, signal, Type, PLATFORM_ID, RESPONSE_INIT, PendingTasks } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT, isPlatformServer, NgComponentOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs/operators';

import { ResolveResult } from '../../core/models/resolve.models';
import { CMS_DOCUMENT_NAVIGATION } from './cms-page.resolver';

/** Persist the guard before navigating so a persistent failure cannot reload forever. */
export function claimChunkReload(storage: Storage | undefined, url: string, now = Date.now()): boolean {
  if (!storage) return false;
  const key = 'samotics:page-chunk-reload';
  try {
    const previous = storage.getItem(key);
    if (previous) {
      try {
        const attempt = JSON.parse(previous) as { url?: string; at?: number };
        if (attempt.url === url && typeof attempt.at === 'number' && now - attempt.at < 60_000) return false;
      } catch { /* Ignore a malformed old marker. */ }
    }
    storage.setItem(key, JSON.stringify({ url, at: now }));
    return true;
  } catch {
    // If session storage is unavailable, show the manual retry instead.
    return false;
  }
}

@Component({
  selector: 'app-cms-page',
  standalone: true,
  templateUrl: './cms-page.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [NgComponentOutlet],
})
export class CmsPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  private readonly pendingTasks = inject(PendingTasks);
  private readonly document = inject(DOCUMENT);
  private readonly documentNavigate = inject(CMS_DOCUMENT_NAVIGATION);

  readonly resolveResult = toSignal(
    this.route.data.pipe(map(d => (d['resolveResult'] as ResolveResult | null) ?? null)),
    { initialValue: null as ResolveResult | null },
  );

  readonly componentType = signal<Type<unknown> | null>(null);
  readonly componentInputs = signal<Record<string, unknown>>({});
  readonly loadFailed = signal(false);

  retryPage(): void {
    this.documentNavigate(this.router.url);
  }

  /**
   * quick-95: dispatch table for lazy page-component loading.
   * Maps `result.type` (and `result.contentType` for the archive-page split)
   * to the dynamic import callback that resolves to the typed component class.
   *
   * Each call returns a Promise<Type<unknown>>; the `effect()` below tracks
   * the promise via PendingTasks so SSR awaits resolution before rendering.
   *
   * IMPORTANT: do NOT add eager static imports back — that would defeat the
   * bundle-split. The 10 page-components are intentionally NOT in `imports: [...]`.
   */
  private resolveComponentLoader(result: ResolveResult): (() => Promise<Type<unknown>>) | null {
    const type = result.type;
    const contentType = (result as { contentType?: string }).contentType;

    switch (type) {
      case 'archive-page':
        if (contentType === 'faq-entry') {
          return () => import('../faq-archive-page/faq-archive-page.component').then(m => m.FaqArchivePageComponent as Type<unknown>);
        }
        return () => import('../archive-page/archive-page.component').then(m => m.ArchivePageComponent as Type<unknown>);
      case 'asset-type-page':
        return () => import('../asset-type-page/asset-type-page.component').then(m => m.AssetTypePageComponent as Type<unknown>);
      case 'industry-page':
        return () => import('../industry-page/industry-page.component').then(m => m.IndustryPageComponent as Type<unknown>);
      case 'insight-page':
      case 'news-page':
        return () => import('../article-page/article-page.component').then(m => m.ArticlePageComponent as Type<unknown>);
      case 'case-study':
        return () => import('../case-study-page/case-study-page.component').then(m => m.CaseStudyPageComponent as Type<unknown>);
      case 'static-page':
        return () => import('../static-page/static-page.component').then(m => m.StaticPageComponent as Type<unknown>);
      case 'landing-page':
      case 'lead-magnet-page':
        return () => import('../landing-page/landing-page.component').then(m => m.LandingPageComponent as Type<unknown>);
      case 'partner':
        return () => import('../partner-page/partner-page.component').then(m => m.PartnerPageComponent as Type<unknown>);
      case 'event-page':
        return () => import('../event-page/event-page.component').then(m => m.EventPageComponent as Type<unknown>);
      case 'webinar-page':
        return () => import('../webinar-page/webinar-page.component').then(m => m.WebinarPageComponent as Type<unknown>);
      default:
        return null;
    }
  }

  constructor() {
    effect(onCleanup => {
      let active = true;
      onCleanup(() => { active = false; });
      const result = this.resolveResult();
      if (!result) return;
      this.loadFailed.set(false);

      // LAND-07 defence-in-depth: even if an archive-page+landing-page result
      // slips past cmsPageResolver, refuse to render it. The resolver should
      // already have caught this and navigated to /404.
      if (
        result.type === 'archive-page' &&
        (result as { contentType?: string }).contentType === 'landing-page'
      ) {
        this.notFound();
        return;
      }

      // quick-60 allowlist defence-in-depth.
      if (!['archive-page', 'asset-type-page', 'case-study', 'industry-page', 'insight-page', 'news-page', 'static-page', 'landing-page', 'lead-magnet-page', 'partner', 'event-page', 'webinar-page'].includes(result.type)) {
        this.notFound();
        return;
      }

      // quick-95: dispatch to the lazy component loader for this result.type.
      const loader = this.resolveComponentLoader(result);
      if (!loader) {
        // Defensive — allowlist above should make this unreachable. Treat as notFound.
        this.notFound();
        return;
      }

      // PendingTasks.run() blocks application stability until the import resolves.
      // On SSR this means renderApplication() awaits the import before serializing,
      // so no flash-of-empty-content reaches the wire. On the browser it makes the
      // app reach stable state only after the chunk arrives, which is the same
      // semantic the eager imports had.
      this.pendingTasks.run(async () => {
        try {
          const cmp = await loader();
          if (!active) return;
          this.componentType.set(cmp);
          this.componentInputs.set({ resolveResult: result });
        } catch (err) {
          if (!active) return;
          console.error('[cms-page] lazy-load failed for result.type=' + result.type, err);
          this.componentType.set(null);
          this.loadFailed.set(true);
          if (isPlatformServer(this.platformId)) {
            // The SSR response boundary converts this to a no-store 503.
            if (this.responseInit) this.responseInit.status = 503;
            return;
          }
          // A tab opened before deployment may request a removed hashed chunk.
          // Load the destination document once to obtain the current asset URLs.
          try {
            if (claimChunkReload(this.document.defaultView?.sessionStorage, this.router.url)) {
              this.retryPage();
            }
          } catch { /* Storage may be blocked; the manual retry stays available. */ }
        }
      });
    });
  }

  /**
   * quick-60 defence-in-depth: mirrors the resolver's `notFound()` helper.
   * On SSR we write a real HTTP 404 via RESPONSE_INIT so soft-404 indexing
   * and edge-cache pollution are prevented even when the resolver missed a
   * branch. Browser path is a plain SPA nav with skipLocationChange.
   */
  private notFound(): void {
    if (isPlatformServer(this.platformId) && this.responseInit) {
      this.responseInit.status = 404;
    }
    this.router.navigate(['/404'], { skipLocationChange: true });
  }
}
